/**
 * 超管变身 (Ricky 2026-07-07 拍板"就是变身, 不要对我做风控")。
 *
 * 固定悬浮于右上 (独立于 owner壳/管理壳, 任何页面可用):
 * - 空闲态(仅超管可见): "模拟身份" 小胶囊 → 弹窗搜人 → 变身;
 * - 变身态(所有人可见, 因为当前 token 已是目标身份): 橙色胶囊
 *   "正在模拟 · 姓名" + 退出 → 还原本尊 token 并整页刷新。
 * 机制: /auth/impersonate 签发目标等价登录 JWT; 本尊 token 备份在
 * localStorage, 退出即还原 — 全程与目标本人登录所见/所能完全一致。
 */
import React, { useCallback, useState } from 'react';
import { UserSwitchOutlined } from '@ant-design/icons';
import { App, Button, Input, List, Modal, Popover, Typography } from 'antd';
import { AuthService, EmployeesService } from '../../.generated/api-client';
import type { EmployeeSearchItem } from '../../.generated/api-client';
import { getAdminToken, setAdminToken } from '../services/api';
import {
  clearAdminImpersonationSession,
  IMPERSONATOR_BACKUP_KEY,
  IMPERSONATING_LABEL_KEY,
  isAdminImpersonating,
} from '../services/impersonationSession';
import './ImpersonationControl.css';
import { positionLabel } from '../utils/position';
import {
  clearImpersonationHistory,
  historyEntryFromEmployee,
  loadImpersonationHistory,
  saveImpersonationHistory,
  type ImpersonationHistoryEntry,
} from './impersonationHistory';

type ImpersonationTarget = EmployeeSearchItem | ImpersonationHistoryEntry;

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function targetEmployeeId(item: ImpersonationTarget): number {
  return 'employee_id' in item ? item.employee_id : item.employeeId;
}

function targetFullName(item: ImpersonationTarget): string {
  return 'full_name' in item ? item.full_name : item.fullName;
}

function targetPositionCn(item: ImpersonationTarget): string {
  return 'position_cn' in item ? positionLabel(item.position, item.position_cn) : positionLabel(item.positionCn, item.positionCn);
}

function targetStoreLabel(item: ImpersonationTarget): string {
  return 'store_label' in item ? item.store_label : item.storeLabel;
}

function targetToHistoryEntry(
  item: ImpersonationTarget,
  actor?: Record<string, unknown>
): ImpersonationHistoryEntry {
  if ('employee_id' in item) {
    return historyEntryFromEmployee(item, actor);
  }
  const actorName = actor?.full_name;
  const actorPosition = actor?.position_cn;
  return {
    employeeId: item.employeeId,
    fullName: typeof actorName === 'string' && actorName ? actorName : item.fullName,
    positionCn: positionLabel(actorPosition, item.positionCn),
    storeLabel: item.storeLabel,
  };
}

export function isImpersonating(): boolean {
  return isAdminImpersonating();
}

// variant: floating=非驾驶舱页右上浮层; inline=历史工具栏胶囊; compact=经营总览图标级入口。
const ImpersonationControl: React.FC<{
  isSuperAdmin: boolean;
  variant?: 'floating' | 'inline' | 'compact';
}> = ({
  isSuperAdmin,
  variant = 'floating',
}) => {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [items, setItems] = useState<EmployeeSearchItem[]>([]);
  const [history, setHistory] = useState<ImpersonationHistoryEntry[]>(() => loadImpersonationHistory());
  const impersonating = isImpersonating();
  const impLabel = localStorage.getItem(IMPERSONATING_LABEL_KEY) ?? '';

  const runSearch = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      if (query.length < 2) {
        message.info('至少输入 2 个字符（姓名或手机号）');
        return;
      }
      setSearching(true);
      try {
        const resp = await EmployeesService.searchEmployeesEndpointApiV1EmployeesSearchGet({
          query,
        });
        const data = asRecord(resp.data);
        setItems((data.items as EmployeeSearchItem[] | undefined) ?? []);
      } catch {
        message.error('搜索失败，请重试');
      } finally {
        setSearching(false);
      }
    },
    [message]
  );

  const impersonate = useCallback(
    async (item: ImpersonationTarget) => {
      setSwitching(true);
      try {
        const resp = await AuthService.impersonateEndpointApiV1AuthImpersonatePost({
          requestBody: { target_employee_id: targetEmployeeId(item) },
        });
        const data = asRecord(resp.data);
        const jwt = data.jwt;
        const actor = asRecord(data.actor);
        if (typeof jwt !== 'string' || !jwt) {
          message.error('变身失败，请重试');
          return;
        }
        // 只在"本尊 → 目标"时备份, 已在模拟态再切人保留最初的本尊备份。
        if (!localStorage.getItem(IMPERSONATOR_BACKUP_KEY)) {
          localStorage.setItem(IMPERSONATOR_BACKUP_KEY, getAdminToken());
        }
        localStorage.setItem(
          IMPERSONATING_LABEL_KEY,
          `${String(actor.full_name ?? targetFullName(item))} · ${positionLabel(actor.role, actor.position_cn ?? targetPositionCn(item))}`
        );
        setHistory(saveImpersonationHistory(targetToHistoryEntry(item, actor)));
        setAdminToken(jwt);
        window.location.assign('/');
      } catch {
        message.error('变身失败（对方可能已停用）');
      } finally {
        setSwitching(false);
      }
    },
    [message]
  );

  const openPicker = useCallback(() => {
    setHistory(loadImpersonationHistory());
    setOpen(true);
  }, []);

  const clearHistory = useCallback(() => {
    clearImpersonationHistory();
    setHistory([]);
  }, []);

  const exitImpersonation = useCallback(() => {
    const backup = localStorage.getItem(IMPERSONATOR_BACKUP_KEY);
    if (backup) {
      setAdminToken(backup);
    }
    clearAdminImpersonationSession();
    window.location.assign('/');
  }, []);

  const compactControl = impersonating ? (
    <Popover
      placement="bottomRight"
      trigger="click"
      content={
        <div className="impersonation-compact-popover">
          <Typography.Text type="secondary">当前模拟身份</Typography.Text>
          <Typography.Text strong>{impLabel || '目标员工'}</Typography.Text>
          <Button type="text" danger size="small" onClick={exitImpersonation}>
            退出模拟身份
          </Button>
        </div>
      }
    >
      <button
        type="button"
        className="impersonation-compact-trigger impersonation-compact-trigger--active"
        aria-label={`正在模拟身份：${impLabel || '目标员工'}`}
      >
        <UserSwitchOutlined />
      </button>
    </Popover>
  ) : (
    <button
      type="button"
      className="impersonation-compact-trigger"
      aria-label="模拟身份"
      onClick={openPicker}
    >
      <UserSwitchOutlined />
    </button>
  );

  if (!impersonating && !isSuperAdmin) return null;

  return (
    <>
      {/* 炉火口径安静胶囊 (Ricky 2026-07-07 "网页端做的非常丑"): 暖纸底+发丝描边+
          火苗小点, 默认停在 header 下方右侧不与切店器/铃铛打架; 变身态整胶囊
          转暖底, 姓名主体 + 退出文字钮。样式在 globals.css .imp-pill 族。 */}
      {variant === 'compact' ? (
        compactControl
      ) : (
        <div
          className={[
            'imp-pill',
            variant === 'floating' ? 'imp-pill--floating' : '',
            impersonating ? 'imp-pill--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {impersonating ? (
            <>
              <span className="imp-pill-dot" />
              <span className="imp-pill-label">
                正在以 <b>{impLabel}</b> 视角
              </span>
              <span className="imp-pill-divider" />
              <button type="button" className="imp-pill-exit" onClick={exitImpersonation}>
                退出
              </button>
            </>
          ) : (
            <button type="button" className="imp-pill-trigger" onClick={openPicker}>
              <span className="imp-pill-dot" />
              模拟身份
            </button>
          )}
        </div>
      )}
      <Modal
        title="以谁的视角查看"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          搜索员工，选中后你将以其身份浏览与操作（等价其本人登录），身份入口可随时退出。
        </Typography.Paragraph>
        <Input.Search
          placeholder="姓名或手机号，至少 2 个字符"
          enterButton="搜索"
          loading={searching}
          onSearch={runSearch}
          autoFocus
        />
        <List
          style={{ marginTop: 12, maxHeight: 320, overflow: 'auto' }}
          dataSource={items}
          locale={{ emptyText: '输入关键词搜索员工' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="primary"
                  size="small"
                  loading={switching}
                  onClick={() => void impersonate(item)}
                >
                  变身
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={`${targetFullName(item)}（${targetPositionCn(item)}）`}
                description={targetStoreLabel(item) || '未挂店（管理岗）'}
              />
            </List.Item>
          )}
        />
        {items.length === 0 && history.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Typography.Text strong>最近模拟</Typography.Text>
              <Button type="link" size="small" onClick={clearHistory}>
                清空
              </Button>
            </div>
            <List
              size="small"
              dataSource={history}
              style={{ maxHeight: 260, overflow: 'auto' }}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="go"
                      size="small"
                      loading={switching}
                      onClick={() => void impersonate(item)}
                    >
                      变身
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={`${targetFullName(item)}${targetPositionCn(item) ? `（${targetPositionCn(item)}）` : ''}`}
                    description={targetStoreLabel(item) || '未挂店（管理岗）'}
                  />
                </List.Item>
              )}
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default ImpersonationControl;
